/**
 * Locale state management backed by localStorage.
 * Tracks user's selected language, country, and currency.
 * Currency is tied to country (e.g., SE → SEK, FI → EUR).
 */

import { getSiteDefaultLocale, resolveLocaleInfo } from "@/utils/locale-url";

const STORAGE_KEY = "wehunt-locale";

export interface LocaleState {
  language: string;
  country: string;
  currency: string;
}

/** Last-resort values for a site with no mapping of its own. */
const FALLBACK_DEFAULTS: LocaleState = {
  language: "sv-SE",
  country: "SE",
  currency: "SEK",
};

/**
 * The market a visitor starts in, resolved per deployment rather than hardcoded
 * to Sweden — these values become the X-Store-* headers, so on wehunt.no a
 * first visit used to announce the Swedish market to a Norway-only store.
 *
 * Resolved lazily (not at module load) because it reads the runtime env config,
 * which is fetched before the app mounts.
 */
function siteDefaults(): LocaleState {
  const language = getSiteDefaultLocale();
  const info = resolveLocaleInfo(language);
  if (!info) return FALLBACK_DEFAULTS;
  return { language, country: info.country, currency: info.currency };
}

let cached: LocaleState | null = null;

/**
 * Returns true if the user has a previously stored locale preference.
 * False on first visit (no localStorage entry).
 */
export function hasStoredLocale(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

export function getLocale(): LocaleState {
  if (cached) return cached;

  const defaults = siteDefaults();

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LocaleState>;
      cached = {
        language: parsed.language || defaults.language,
        country: parsed.country || defaults.country,
        currency: parsed.currency || defaults.currency,
      };
      return cached;
    }
  } catch {
    // Corrupted or unavailable localStorage — use defaults
  }

  cached = { ...defaults };
  return cached;
}

export function setLocale(update: Partial<LocaleState>): LocaleState {
  const current = getLocale();
  const next: LocaleState = {
    language: update.language ?? current.language,
    country: update.country ?? current.country,
    currency: update.currency ?? current.currency,
  };

  cached = next;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage full or unavailable — state still cached in memory
  }

  return next;
}
